import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { searchAndSortByRelevance } from 'webapp/utils/filter';
import { AdvancedSortProps } from 'webapp/utils/metric-sort';

import makeArray from 'webapp/helpers/make-array';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import MetricLibraryGridItemComponent, { Size } from 'webapp/components/metric-library/grid-item';
import SubmitButtonComponent from 'webapp/components/submit-button';
import WizardSearchBrowseBannerComponent from 'webapp/components/wizard/search-browse-banner';

import type { Metric, SourceType } from 'webapp/models-custom';
import BatchSelect from 'webapp/models-custom/batch-select';

import { StepId } from 'webapp/services/wizard';

type Args = Record<string, never>;

export default class WizardMetricsComponent extends Component<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('flow/tour/onboarding')
	declare flowTourOnboardingService: Service.Flow.Tour.Onboarding;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('wizard') declare wizardService: Service.Wizard;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('logger') declare loggerService: Service.Logger;

	@tracked typedFilter = '';
	@tracked selectedSourceKey: string | null = null;
	@tracked submitInProgress = false;
	@tracked availableMetricsFromWizard: Metric[];

	maxItemsInRow = 7;

	defaultSort = AdvancedSortProps;
	searchRelevance = {
		name: 5,
		description: {
			weight: 1,
			exactMatch: true,
		},
		key: 1,
	};

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const preselectedItems: Metric[] = [],
			selectedMetricsBySourceKey = this.selectedMetricsBySourceKey;

		if (selectedMetricsBySourceKey) {
			Object.keys(selectedMetricsBySourceKey).forEach((sourceKey) => {
				preselectedItems.push(...selectedMetricsBySourceKey[sourceKey]!);
			});
		}

		const metricsBatchSelect = new BatchSelect({
			getAllItems: () => {
				return [];
			},
			getOptions: () => {
				return [];
			},
			doBatchAction: () => {
				return Promise.resolve();
			},
			preselectedItems,
			maxSelectedItems: 32,
			onMaxItemsReached: () => {
				this.infolayerService.error(
					'Maximum of 32 metrics available on a single dashboard',
				);
			},
			onItemAdded: (item) => {
				const sourceKey = item.dataSourceTypeKey;

				if (sourceKey) {
					if (!this.selectedMetricsBySourceKey[sourceKey]) {
						this.selectedMetricsBySourceKey[sourceKey] = [];
					}

					if (!this.selectedMetricsBySourceKey[sourceKey].includes(item)) {
						this.selectedMetricsBySourceKey[sourceKey].push(item);
					}
				}
			},
			onItemRemoved: (item) => {
				const sourceKey = item.dataSourceTypeKey;

				if (sourceKey) {
					if (!this.selectedMetricsBySourceKey[sourceKey]) {
						this.selectedMetricsBySourceKey[sourceKey] = [];
					}

					this.selectedMetricsBySourceKey[sourceKey] = this.selectedMetricsBySourceKey[
						sourceKey
					].filter((i) => i !== item);
				}
			},
		});

		this.wizardService.metricsBatchSelect = metricsBatchSelect;

		this.availableMetricsFromWizard = this.wizardService.metricsForSelectedSource;

		const initialSourceKey = this.wizardService.selectedSourceType?.key;
		if (initialSourceKey) {
			this.selectedSourceKey = initialSourceKey;
		}
	}

	get smallSize() {
		return Size.SMALL;
	}

	get batchSelect() {
		return this.wizardService.metricsBatchSelect;
	}

	get selectedSpecificSourceId() {
		return this.wizardService.selectedSpecificSourceId;
	}

	get selectedMetricsCount(): number {
		return this.batchSelect?.selectedItems.length || 0;
	}

	get selectedMetricsBySourceKey() {
		return this.wizardService.selectedMetricsBySourceKey;
	}

	get isNextButtonDisabled(): boolean {
		return this.selectedMetricsCount === 0;
	}

	get allowSwitchToCustomMetricFlow(): boolean {
		return !!this.genericSource?.isCustomMetricWizardPossible;
	}

	get genericSource() {
		const selectedSourceKey = this.selectedSourceKey;
		return selectedSourceKey
			? this.mmSourceTypesService.findModelByKey(selectedSourceKey)
			: null;
	}

	get sources() {
		let sourceTypes: SourceType[] = [];

		const selectedSourceKey = this.selectedSourceKey;
		if (this.selectedSpecificSourceId && selectedSourceKey) {
			const sourceType = this.mmSourceTypesService.findModelByKey(selectedSourceKey);

			if (sourceType) {
				sourceTypes = [sourceType];
			}
		} else {
			sourceTypes = this.mmSourceTypesService.connectableSourceTypeModels.filter(
				(item) => item.hasBasicMetrics,
			);
		}

		return sourceTypes.map((source) =>
			this.mmSourcesService.formatSourceTypeForList(source, 'Data sources'),
		);
	}

	get availableMetrics() {
		return this.availableMetricsFromWizard;
	}

	get filteredMetrics() {
		// if user searches, we don't want to sort by AdvancedSortProps
		const defaultSort = this.typedFilter ? null : this.defaultSort;

		return searchAndSortByRelevance(
			this.availableMetrics,
			this.searchRelevance,
			defaultSort,
			this.typedFilter,
		);
	}

	get selectedMetricsNotInFilteredResults() {
		let metrics: Metric[] = [];

		// only return when filter is active
		if (this.typedFilter) {
			metrics = this.availableMetrics.filter((metric) => {
				return (
					this.batchSelect?.selectedItems.includes(metric) &&
					!this.filteredMetrics.includes(metric)
				);
			});
		}

		return metrics;
	}

	get noSearchResults() {
		return this.typedFilter && !this.filteredMetrics.length;
	}

	get isAvailableMetricsSectionShown(): boolean {
		return !!this.filteredMetrics.length;
	}

	@action
	selectMetric(model: Metric) {
		this.flowTourOnboardingService.selectMetrics(model.id!); // store generic metrickeys
	}

	@action
	selectSource(key: string) {
		this.selectedSourceKey = key;
		const source = this.wizardService.availableSources.find((item) => item.key === key);
		this.availableMetricsFromWizard = source
			? this.wizardService.getMetricsForSource(source)
			: [];
	}

	@action
	clearSelection() {
		this.batchSelect?.unselectAll();
		this.wizardService.selectedMetricsBySourceKey = {};
	}

	@action
	switchToCustomMetricFlow() {
		const sourceId = this.wizardService.selectedSpecificSourceId;

		if (sourceId) {
			this.mmCustomQueriesService.openCreateNewPopup(
				'wizard',
				() => {
					const sourceType = this.wizardService.selectedSourceType;

					this.availableMetricsFromWizard =
						this.wizardService.getMetricsForSource(sourceType); // refresh available metrics to get created custom query

					schedule('afterRender', this, () => {
						const lastSelectableItem = document.querySelector<HTMLElement>(
							'li.card--selectable:last-child',
						);

						if (lastSelectableItem) {
							lastSelectableItem.click(); // "hack" to trigger last selectable item after custom query creation (because some logic is also in wizard-metrics action "selectMetric")
						}
					});
				},
				+sourceId || undefined,
			);
		} else {
			this.wizardService.hasUserChosenQbFlow = true;
			this.wizardService.goToStep(StepId.CONNECT);
		}
	}

	@action
	changeSearch(value: string) {
		this.typedFilter = value;
	}

	@action
	next() {
		//const selectedMetricIds = this.batchSelect?.selectedItems.map((item) => item.id);

		const selectedMetrics = this.batchSelect?.selectedItems;

		const origin = 'wizard',
			metrics = (selectedMetrics || []).map((metric) => metric.name);

		this.analyticsService.event(TrackingEvent.ONBOARDING_METRICS_CHOSEN, {
			origin,
			metrics,
		});

		if (this.wizardService.selectedSpecificSourceId && selectedMetrics) {
			this.submitInProgress = true;

			const layer = this.infolayerService.loading('Building dashboard...');
			this.mmBoardsService
				.createBoardFromMetrics(selectedMetrics)
				.then((board) => {
					const boardModel = this.mmBoardsService.findModel(String(board.id));

					if (boardModel) {
						const datasources = Object.keys(this.selectedMetricsBySourceKey);
						this.mmBoardsService.triggerDataboardCreated(
							{ from: 'wizard_metrics', datasources },
							boardModel,
						);
					}

					this.analyticsService.event(TrackingEvent.DATABOARD_WIZARD_METRICS_SUCCESS);

					layer.ok('Dashboard successfully built!');
					this.wizardService.setCreatedBoard(board);
					void this.wizardService.goToNext();
				})
				.catch((error) => {
					this.loggerService.logError(`[Wizard] Failed auto generating board`, {
						error: JSON.stringify(error),
					});
					layer.close();
				})
				.finally(() => {
					this.submitInProgress = false;
				});
		} else {
			void this.wizardService.goToNext();
		}
	}

	@action
	back() {
		this.wizardService.goToPrevious();
	}
	<template>
		<WizardSearchBrowseBannerComponent
			@searchTypedFilter={{this.typedFilter}}
			@changeSearch={{this.changeSearch}}
			@browseName="source"
			@browseSelected={{this.selectedSourceKey}}
			@browseOptions={{this.sources}}
			@onBrowseChange={{this.selectSource}}
			@hideBrowse={{false}}
		/>

		<div class="content">

			<div class="list">

				<div class="scroll">

					{{#if this.isAvailableMetricsSectionShown}}
						<div class="metrics-wrapper metrics-wrapper-metrics-per-source-type">
							<span class="label">AVAILABLE METRICS</span>

							<ul
								class="flex-ct flex-ct--size-block-metric flex-ct--size-block-metric-condensed"
							>
								{{#each this.selectedMetricsNotInFilteredResults as |metric|}}
									<MetricLibraryGridItemComponent
										@model={{metric}}
										@canSelect={{true}}
										@size={{this.smallSize}}
										@hideActions={{true}}
										@isVisibleOnScreen={{true}}
										@isStatusHidden={{true}}
										@batchSelect={{this.batchSelect}}
										@onSelectMetric={{this.selectMetric}}
									/>
								{{/each}}
								{{#each this.filteredMetrics as |metric|}}
									<MetricLibraryGridItemComponent
										@model={{metric}}
										@canSelect={{true}}
										@size={{this.smallSize}}
										@hideActions={{true}}
										@isVisibleOnScreen={{true}}
										@isStatusHidden={{true}}
										@batchSelect={{this.batchSelect}}
										@onSelectMetric={{this.selectMetric}}
									/>
								{{/each}}

								{{#if this.allowSwitchToCustomMetricFlow}}
									<li
										role="button"
										class="card card--small-metric-type card--new flex-item filter-all"
										{{on "click" this.switchToCustomMetricFlow}}
									>
										<span>+</span>
									</li>
								{{/if}}

								{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
								{{#each (makeArray length=this.maxItemsInRow)}}
									<li class="flex-item flex-item--fake"></li>
								{{/each}}

							</ul>

						</div>

					{{/if}}

					{{#if this.noSearchResults}}
						<div class="noResults">
							<CommonUiIconComponent
								@name="Search"
								@size="2xl"
								@class="noResults-icon"
							/>
							No results found for "<strong>{{this.typedFilter}}</strong>".
						</div>
					{{/if}}

				</div>

			</div>

			<div class="wizard-controls smart-center-pos">
				<div class="container">
					<div class="center-container center-container--metrics">
						<div class="selected-metrics">
							Selected metrics:
							<strong>{{this.selectedMetricsCount}}</strong>
							<a
								role="button"
								class="btn btn-clear"
								{{on "click" this.clearSelection}}
							>
								Clear selection
							</a>
						</div>
						<SubmitButtonComponent
							@text="Continue"
							@extraClass="next"
							@size="big"
							@isDisabled={{this.isNextButtonDisabled}}
							@submitInProgress={{this.submitInProgress}}
							@noDelay={{true}}
							@onTrigger={{this.next}}
						/>
					</div>

					<a role="button" class="btn back" {{on "click" this.back}}>
						<CommonUiIconComponent @name="ArrowLeft" @size="xs" />
						Go back
					</a>

				</div>
			</div>

		</div>
	</template>
}
